<script>
import Icon from '@/components/Icon'

export default {
  name: 'RadioField',

  components: {
    Icon,
  },

  model: {
    prop: 'modelValue',
    event: 'change',
  },

  props: {
    label: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    value: {
      type: String,
      default: null,
    },

    isGroup: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: String,
      default: null,
    },

    // 'default' = ícone circle/dot-circle + label
    // 'segmented' = pílula (segmented control), com ícone opcional via prop `icon`
    variant: {
      type: String,
      default: 'default',
      validator: v => ['default', 'segmented'].includes(v),
    },

    // Ícone customizado exibido no modo segmented (nome de src/assets/icons)
    icon: {
      type: String,
      default: null,
    },
  },

  computed: {
    mutableValue: {
      get () {
        return this.modelValue
      },
      set (value) {
        this.$emit('change', value)
      },
    },

    isSegmented () {
      return this.variant === 'segmented'
    },

    computedClasses () {
      return {
        [this.$classes['form-radio-item']]: true,
        [this.$classes['has-parent']]: this.isGroup,
        [this.$classes['is-disabled']]: this.disabled,
        [this.$classes['is-checked']]: this.isChecked,
        [this.$classes['is-segmented']]: this.isSegmented,
      }
    },

    isChecked () {
      return this.modelValue === this.value
    },

    iconName () {
      return this.isChecked ? 'dot-circle' : 'circle'
    },

    // No segmented mostra o ícone customizado (se houver); senão o circle/dot-circle.
    displayIcon () {
      return this.isSegmented ? this.icon : this.iconName
    },
  },
}
</script>

<template>
  <div :class="computedClasses">

    <label
      :class="$classes['form-radio-label']"
    >
      <input
        type="radio"
        :class="$classes['form-radio']"
        :disabled="disabled"
        :readonly="readonly"
        :value="value"
        v-model="mutableValue"
      >
      <icon
        v-if="displayIcon"
        :name="displayIcon"
        :class="$classes['form-radio-icon']"
      />
      <span
        v-if="label"
        :class="$classes['form-radio-label-text']"
      >
        {{ label }}
      </span>
    </label>
  </div>
</template>

<style module>
.form-radio-item {
  position: relative;
}

.form-radio-item + .form-radio-item {
  margin-top: 15px;
}

.form-radio-item.has-parent + .form-radio-item.has-parent {
  margin-top: 0;
  margin-left: 15px;
}

.form-radio {
  position: absolute;
  visibility: hidden;
}

.form-radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #a1a1a1;
}

.form-radio-label-text {
  font-size: 16px;
  margin-left: 10px;
}

.form-radio-item .form-radio-icon {
  width: 18px;
  height: 18px;
}

.form-radio-item.is-checked .form-radio-label {
  color: #292929;
}

.form-radio-item.is-disabled .form-radio-label {
  cursor: not-allowed;
  color: #49507C;
  opacity: 0.8;
}

/* =========================================================
   Variante "segmented" (segmented control / pílula)
   O trilho cinza fica no .form-radio-group.is-segmented (RadioGroup);
   aqui estilizamos cada segmento.
   ========================================================= */
.form-radio-item.is-segmented {
  margin: 0;
}

/* o espaçamento entre segmentos vem do gap do grupo */
.form-radio-item.is-segmented.has-parent + .form-radio-item.is-segmented.has-parent {
  margin-top: 0;
  margin-left: 0;
}

.form-radio-item.is-segmented .form-radio-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  min-height: 31px;
  border-radius: 6px;
  /* borda transparente nos inativos: evita "pulo" de 1px quando o ativo ganha borda */
  border: 1px solid transparent;
  color: #5c5c5c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transition: background 180ms cubic-bezier(.2, .7, .3, 1),
    color 180ms cubic-bezier(.2, .7, .3, 1),
    border-color 180ms cubic-bezier(.2, .7, .3, 1),
    box-shadow 180ms cubic-bezier(.2, .7, .3, 1);
}

.form-radio-item.is-segmented:not(.is-checked):not(.is-disabled) .form-radio-label:hover {
  color: #292929;
}

.form-radio-item.is-segmented.is-checked .form-radio-label {
  background: #ffffff;
  color: #1a1a1a;
  border-color: #e2e2e2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.04);
}

.form-radio-item.is-segmented .form-radio-label-text {
  margin-left: 0;
  font-size: 12px;
}

.form-radio-item.is-segmented .form-radio-icon {
  width: 13px;
  height: 13px;
}

.form-radio-item.is-segmented.is-disabled .form-radio-label {
  cursor: not-allowed;
  opacity: 0.6;
  color: #5c5c5c;
}

@media (max-width: 767px) {
  .form-radio-item.has-parent + .form-radio-item.has-parent {
    width: 100%;
    margin-top: 15px;
    margin-left: 0;
  }

  /* no segmented os segmentos continuam lado a lado mesmo no mobile */
  .form-radio-item.is-segmented.has-parent + .form-radio-item.is-segmented.has-parent {
    width: auto;
    margin-top: 0;
    margin-left: 0;
  }
}
</style>
